import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, UserRound } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import StaffIdInput from '../../../components/StaffIdInput';
import apiClient from '../../../services/apiClient';
import { PpBatchScanPanel } from '../../../components/comboV2/PpBatchScanPanel';

const P = '/combo-v2/pp-station';
const combos = (s) => [s.design_name, s.shade, s.size].filter(Boolean).join(' / ');

export default function ComboV2PPStation() {
  const navigate = useNavigate();
  const [staff, setStaff] = useState(null);           // {staffId, staffName}
  const [handover, setHandover] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [floorMap, setFloorMap] = useState(null);     // {usn: component_barcode}
  const [floorLoading, setFloorLoading] = useState(false);
  const [items, setItems] = useState([]);             // [{usn, component_barcode, problem}]
  const [banner, setBanner] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadHandover = async (sid) => {
    setLoading(true);
    try {
      const r = await apiClient.get(`${P}/my-handover`, { params: { staff_id: sid } });
      setHandover(r.data.handover);
      const s = r.data.handover?.plan_id ? await apiClient.get(`${P}/plans/${r.data.handover.plan_id}/sessions`) : null;
      setSessions(s?.data?.sessions || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load handover');
    } finally { setLoading(false); }
  };

  const onStaff = (v) => {
    if (!v?.valid) return;
    setStaff({ staffId: v.staffId, staffName: v.staffName });
    loadHandover(v.staffId);
  };
  const resetStaff = () => { setStaff(null); setHandover(null); setSessions([]); setSelectedSession(null); setItems([]); setBanner(''); };

  const pickSession = async (s) => {
    setSelectedSession(s); setItems([]); setBanner(''); setFloorMap(null); setFloorLoading(true);
    try {
      const r = await apiClient.get(`${P}/sessions/${s.session_id}/floor-usns`);
      setFloorMap(r.data.usns || {});
    } catch (e) {
      toast.error('Could not load floor USNs — counts will show after Save');
    } finally { setFloorLoading(false); }
  };
  const leaveSession = () => { setSelectedSession(null); setItems([]); setBanner(''); setFloorMap(null); };

  const save = async () => {
    setBanner(''); setSubmitting(true);
    try {
      const r = await apiClient.post(`${P}/submit`, {
        session_id: selectedSession.session_id, handover_id: handover.handover_id, scanned_usns: items.map((i) => i.usn),
      });
      toast.success(`Saved ${r.data.combos_created} combo(s). Print the stickers at the Sticker Print Desk (staff ID ${r.data.pp_staff_id || staff.staffId}).`, { duration: 8000 });
      const saved = new Set(items.map((i) => i.usn));
      setFloorMap((m) => (m ? Object.fromEntries(Object.entries(m).filter(([u]) => !saved.has(u))) : m));
      setItems([]);
      if (r.data.session_status === 'COMPLETED') leaveSession();
      loadHandover(staff.staffId);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.problems) {
        const why = Object.fromEntries(d.problems.map((p) => [p.usn, p.reason]));
        setItems((p) => p.map((i) => ({ ...i, problem: why[i.usn] || null })));
        setBanner(d.message);
      } else {
        setBanner(typeof d === 'string' ? d : 'Save failed — please try again');
      }
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5] p-6" data-testid="pp-station-page">
      <div className="max-w-[1100px] mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="outline" size="sm" onClick={() => navigate('/wms/combo-v2')} data-testid="back-btn"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
          <h1 className="text-2xl font-bold text-stone-900">Combo PP Scan Station</h1>
        </div>

        <Card className="p-4 mb-4" data-testid="pp-staff-card">
          {staff ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><UserRound className="h-5 w-5 text-emerald-700" />
                <span className="font-semibold" data-testid="pp-staff-name">{staff.staffName}</span>
                <span className="font-mono text-xs text-stone-500">{staff.staffId}</span>
              </div>
              <Button size="sm" variant="outline" onClick={resetStaff} data-testid="change-staff-btn">Change staff</Button>
            </div>
          ) : (
            <div className="max-w-md"><StaffIdInput onValidated={onStaff} autoFocus label="Scan your Staff ID" /></div>
          )}
        </Card>

        <Card className="p-3 mb-4 bg-emerald-50 border-emerald-200 text-sm text-emerald-800" data-testid="print-desk-hint">
          Scanning only saves the combos. Stickers are printed at the <b>Sticker Print Desk</b> by entering your staff ID.
        </Card>

        {!staff ? (
          <Card className="p-12 text-center text-stone-500" data-testid="pp-scan-staff-prompt">Scan your staff ID above to load your handover and start assembling.</Card>
        ) : loading ? (
          <div className="p-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : !handover ? (
          <Card className="p-12 text-center" data-testid="no-handover">
            <div className="text-stone-500 mb-2">No active handover for {staff.staffName} ({staff.staffId}).</div>
            <div className="text-xs text-stone-400">Wait for the Piece Distributor to hand over pieces.</div>
          </Card>
        ) : (
          <>
            <Card className="p-4 mb-4 bg-amber-50 border-amber-300">
              <div className="flex justify-between items-center">
                <div><div className="text-xs text-stone-600">Active Handover</div><div className="font-mono text-sm">{handover.handover_id}</div></div>
                <div className="text-right"><div className="text-xs text-stone-600">Assigned</div><div className="text-2xl font-bold">{handover.qty_assigned}</div></div>
                <div className="text-right"><div className="text-xs text-stone-600">Scanned</div><div className="text-2xl font-bold text-green-700">{handover.qty_scanned}</div></div>
                <div className="text-right"><div className="text-xs text-stone-600">Remaining</div><div className="text-2xl font-bold text-amber-700" data-testid="handover-remaining">{handover.qty_assigned - handover.qty_scanned}</div></div>
              </div>
            </Card>

            {!selectedSession ? (
              <Card>
                <div className="p-4 border-b font-semibold">Select a Session</div>
                <table className="w-full text-sm" data-testid="session-select-table">
                  <thead className="bg-stone-50">
                    <tr>
                      <th className="text-left p-3">Session</th><th className="text-left p-3">Combo</th><th className="text-left p-3">Design / Shade / Size</th>
                      <th className="text-right p-3">Target</th><th className="text-right p-3">Done</th><th className="text-right p-3">Remaining</th><th className="p-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.filter((s) => s.remaining > 0).map((s) => (
                      <tr key={s.session_id} className="border-t hover:bg-stone-50">
                        <td className="p-3 font-mono text-xs">{s.session_id}</td>
                        <td className="p-3">{s.combo_barcode}</td>
                        <td className="p-3"><div>{combos(s) || '—'}</div>{s.description && <div className="text-xs text-stone-500">{s.description}</div>}</td>
                        <td className="p-3 text-right">{s.target_quantity}</td>
                        <td className="p-3 text-right">{s.assembled_quantity}</td>
                        <td className="p-3 text-right font-bold">{s.remaining}</td>
                        <td className="p-3"><Button size="sm" onClick={() => pickSession(s)} data-testid={`pick-${s.session_id}`}>Pick</Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            ) : (
              <Card>
                <div className="p-4 border-b flex items-center justify-between">
                  <div>
                    <div className="font-semibold">{selectedSession.combo_barcode}</div>
                    <div className="text-xs text-stone-500">{combos(selectedSession)}{selectedSession.description ? ` • ${selectedSession.description}` : ''}</div>
                    <div className="text-xs text-stone-500 mt-1">Components: {(selectedSession.components || []).map((c) => c.component_name || c.component_barcode).join(', ')}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={leaveSession} data-testid="change-session-btn">Change session</Button>
                </div>
                <PpBatchScanPanel session={selectedSession} floorMap={floorMap} floorLoading={floorLoading} items={items} setItems={(f) => { setItems(f); setBanner(''); }}
                  banner={banner} submitting={submitting} onSave={save} />
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}
